/** Public PKCE request data only. The verifier stays in the desktop process. */
export interface DesktopAuthorizationRequest {
  redirect_uri: string;
  code_challenge: string;
  code_challenge_method: "S256";
  state: string;
}

const REQUEST_KEYS = ["redirect_uri", "code_challenge", "code_challenge_method", "state"];
const BASE64URL_43 = /^[A-Za-z0-9_-]{43}$/;

export function parseDesktopAuthorization(search: string): DesktopAuthorizationRequest | null {
  const params = new URLSearchParams(search);
  if (Array.from(params.keys()).some(key => !REQUEST_KEYS.includes(key))) return null;
  if (REQUEST_KEYS.some(key => params.getAll(key).length !== 1)) return null;

  const redirect_uri = params.get("redirect_uri")!;
  const callback = /^http:\/\/127\.0\.0\.1:([1-9]\d{0,4})\/callback$/.exec(redirect_uri);
  if (!callback || Number(callback[1]) > 65535) return null;
  const code_challenge = params.get("code_challenge")!;
  const state = params.get("state")!;
  if (!BASE64URL_43.test(code_challenge) || !/^[A-Za-z0-9_-]{32,128}$/.test(state)) return null;
  if (params.get("code_challenge_method") !== "S256") return null;
  return { redirect_uri, code_challenge, code_challenge_method: "S256", state };
}

/** Never follow an API response to a different host, path, or login attempt. */
export function validateDesktopAuthorizationRedirect(request: DesktopAuthorizationRequest, value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const target = new URL(value);
    if (`${target.origin}${target.pathname}` !== request.redirect_uri || target.username || target.password || target.hash) return null;
    if (Array.from(target.searchParams.keys()).some(key => key !== "code" && key !== "state")) return null;
    if (target.searchParams.getAll("code").length !== 1 || target.searchParams.getAll("state").length !== 1) return null;
    if (!BASE64URL_43.test(target.searchParams.get("code")!) || target.searchParams.get("state") !== request.state) return null;
    return target.href;
  } catch {
    return null;
  }
}
